<template>
  <div data-allow-mismatch>
    <ThemedComponent
      v-if="banners"
      path="Animation"
      component-name="Banner"
      :model-value="banners"
    />
    <LazyThemedComponent
      v-for="section in homeSections"
      :key="`section-${section.code}`"
      path="Section"
      :component-name="getSectionComponentName(section.code)"
      :model-value="section"
    />
  </div>
</template>

<script setup lang="ts">
import { MENU_CODE, SECTION_CODE } from '~/constants/menu'
import { LANGUAGES } from '~/constants/language'

definePageMeta({
  alias: LANGUAGES.map(lang => `/${lang}`),
})

const { data: banners } = await useBannerApi()
const { data: sections } = await useHomeSection()
const themeStyleStore = useThemeStyleStore()

const menuStore = useMenuStore()
const homeSections = computed(() => sections.value?.filter((homeSection: HomeSection<unknown>) => {
  const menus = menuStore.list
  const content = homeSection.content
  const isHasContentAndNotEmptyTitle = Array.isArray(content) && content.length > 0 && homeSection.title !== ''
  const isExtraInfoContentVisible = Array.isArray(content) &&
    content.some((item) => typeof item?.description === 'string' && item.description.trim() !== '')
  const MENU_EMPTY = {
    [SECTION_CODE.INTRODUCE]: (content as Introduce).title !== '',
    [SECTION_CODE.OVERVIEW_PRODUCT]: true,
    [SECTION_CODE.EXTRA_INFO]: isExtraInfoContentVisible,
    [SECTION_CODE.PROMOTION]: menus.some(menu => menu.code === MENU_CODE.PROMOTION) && isHasContentAndNotEmptyTitle,
    [SECTION_CODE.PRODUCT]: menus.some(menu => menu.code === MENU_CODE.PRODUCT) && isHasContentAndNotEmptyTitle,
    [SECTION_CODE.VIDEO]: menus.some(menu => (menu.code === MENU_CODE.GALLERY_REVIEW &&
            menu.subMenus.some(subMenu => subMenu.code === MENU_CODE.VIDEO && subMenu.title !== '' && subMenu.slug !== '')) || (menu.code === MENU_CODE.VIDEO)) && isHasContentAndNotEmptyTitle,
    [SECTION_CODE.GALLERY]: menus.some(menu => (menu.code === MENU_CODE.GALLERY_REVIEW &&
            menu.subMenus.some(subMenu => subMenu.code === MENU_CODE.GALLERY && subMenu.title !== '' && subMenu.slug !== '')) || (menu.code === MENU_CODE.GALLERY)) && isHasContentAndNotEmptyTitle,
    [SECTION_CODE.CLIENT]: true,
    [SECTION_CODE.ARTICLE]: menus.some(menu => menu.code === MENU_CODE.ARTICLE) && isHasContentAndNotEmptyTitle,
    [SECTION_CODE.FAQ]: isHasContentAndNotEmptyTitle,
  }
  return MENU_EMPTY[homeSection.code]
}) || [])

const homepageFaqs = computed(() => {
  const section = homeSections.value.find((item: HomeSection<unknown>) => item.code === SECTION_CODE.FAQ)
  return Array.isArray(section?.content) ? section.content.filter((item: any) => item.question && item.answer) : []
})

useHead(() => homepageFaqs.value.length ? {
  script: [{
    type: 'application/ld+json',
    children: JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: homepageFaqs.value.map((item: any) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer },
      })),
    }),
  }],
} : {})

useHead(() => ({
  meta: [
    {
      name: 'theme-name',
      content: themeStyleStore.frame,
    },
  ],
}))

const getSectionComponentName = (code: string) => {
  const componentMap: Record<string, string> = {
    [SECTION_CODE.INTRODUCE]: 'Introduce',
    [SECTION_CODE.OVERVIEW_PRODUCT]: 'OverviewProduct',
    [SECTION_CODE.EXTRA_INFO]: 'ExtraInfo',
    [SECTION_CODE.PROMOTION]: 'Promotion',
    [SECTION_CODE.PRODUCT]: 'Product',
    [SECTION_CODE.VIDEO]: 'Video',
    [SECTION_CODE.GALLERY]: 'Gallery',
    [SECTION_CODE.CLIENT]: 'Client',
    [SECTION_CODE.ARTICLE]: 'Article',
    [SECTION_CODE.FAQ]: 'Faq',
  }
  return componentMap[code] || ''
}

const firstBanner = banners.value?.[0]
const bannerImage = firstBanner?.desktopImage?.large

if (firstBanner) {
  useHead({
    link: [
      ...(firstBanner.desktopImage?.large ? [{
        rel: 'preload' as const,
        as: 'image' as const,
        href: firstBanner.desktopImage.large,
        media: '(min-width: 768px)',
        fetchpriority: 'high',
      }] : []),
      ...(firstBanner.mobileImage?.large ? [{
        rel: 'preload' as const,
        as: 'image' as const,
        href: firstBanner.mobileImage.large,
        media: '(max-width: 767px)',
        fetchpriority: 'high',
      }] : []),
    ],
  })
}

const { t } = useI18n()
const { getPageInfo } = useNavigationMenu()
const pageInfo = getPageInfo(MENU_CODE.HOME)
useCustomSeo({
  title: pageInfo?.metaTitleFront || pageInfo?.title || t('global.loading'),
  image: pageInfo?.ogImage?.large || bannerImage,
  description: pageInfo?.metaDescriptionFront || pageInfo?.description || '',
})
</script>
